import React from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo, { faqJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs, FaqBlock, CtaBanner, RelatedLinks } from "@/pages/seo/SeoLanding";
import NotFoundPage from "@/pages/NotFound";
import { RATGEBER } from "@/data/ratgeber";
import { BookOpen, Clock } from "lucide-react";

export function RatgeberIndex() {
    const crumbs = [{ name: "Start", path: "/" }, { name: "Ratgeber", path: "/ratgeber" }];
    return (
        <PublicLayout>
            <Seo
                title="Bewerbungs-Ratgeber – Lebenslauf, Anschreiben & KI-Tipps | Bewerbung24.ai"
                description="Der große Bewerbungs-Ratgeber: Lebenslauf schreiben, Anschreiben formulieren, ATS verstehen, Lücken erklären und Bewerbung mit KI erstellen — praxisnah erklärt."
                path="/ratgeber"
                jsonLd={breadcrumbJsonLd(crumbs)}
            />
            <section className="max-w-5xl mx-auto px-6 lg:px-8 py-14" data-testid="ratgeber-index">
                <Breadcrumbs items={crumbs} />
                <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mt-6">Bewerbungs-Ratgeber</h1>
                <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                    Antworten auf die wichtigsten Fragen rund um Bewerbung, Lebenslauf, Anschreiben und KI — von Recruiting-Praktikern geprüft, ohne Floskeln.
                </p>
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {RATGEBER.map((a) => (
                        <Link key={a.slug} to={`/ratgeber/${a.slug}`} className="card-elev p-5 hover:shadow-lg transition-shadow group" data-testid={`ratgeber-card-${a.slug}`}>
                            <BookOpen size={18} className="text-red-500" />
                            <p className="mt-3 font-heading font-bold text-slate-900 text-sm leading-snug group-hover:text-red-600 transition-colors">{a.title}</p>
                            <p className="mt-2 text-xs text-slate-500 leading-relaxed line-clamp-2">{a.description}</p>
                            <p className="mt-3 text-[11px] text-slate-400 inline-flex items-center gap-1"><Clock size={10} /> {a.lesezeit} Min. Lesezeit</p>
                        </Link>
                    ))}
                </div>
                <CtaBanner />
            </section>
        </PublicLayout>
    );
}

export function RatgeberArticle() {
    const { slug } = useParams();
    const a = RATGEBER.find((r) => r.slug === slug);
    if (!a) return <NotFoundPage />;
    const crumbs = [{ name: "Start", path: "/" }, { name: "Ratgeber", path: "/ratgeber" }, { name: a.title, path: `/ratgeber/${a.slug}` }];
    const jsonLd = [
        breadcrumbJsonLd(crumbs),
        {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: a.title,
            description: a.description,
            author: { "@type": "Organization", name: "Bewerbung24.ai" },
            publisher: { "@type": "Organization", name: "Bewerbung24.ai" },
            mainEntityOfPage: `https://www.bewerbung24.ai/ratgeber/${a.slug}`,
        },
    ];
    if (a.faq?.length) jsonLd.push(faqJsonLd(a.faq));
    const related = (a.related || [])
        .map((slug2) => RATGEBER.find((r) => r.slug === slug2))
        .filter(Boolean)
        .map((r) => ({ name: r.title, path: `/ratgeber/${r.slug}` }));
    return (
        <PublicLayout>
            <Seo title={`${a.title} | Bewerbung24.ai Ratgeber`} description={a.description} path={`/ratgeber/${a.slug}`} jsonLd={jsonLd} />
            <article className="max-w-3xl mx-auto px-6 lg:px-8 py-14" data-testid={`ratgeber-article-${a.slug}`}>
                <Breadcrumbs items={crumbs} />
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-6 leading-tight">{a.title}</h1>
                <p className="mt-3 text-sm text-slate-400 inline-flex items-center gap-1.5"><Clock size={12} /> {a.lesezeit} Min. Lesezeit · Bewerbung24.ai Ratgeber</p>
                <p className="mt-5 text-lg text-slate-600 leading-relaxed">{a.description}</p>
                {a.sections.map((s, i) => (
                    <section key={i} className="mt-10">
                        <h2 className="font-heading text-2xl font-bold text-slate-900">{s.h}</h2>
                        {s.p.map((para, j) => (
                            <p key={j} className="mt-3 text-slate-700 leading-relaxed">{para}</p>
                        ))}
                        {s.list && (
                            <ul className="mt-3 space-y-1.5 list-disc pl-5 text-slate-700">
                                {s.list.map((li) => <li key={li} className="leading-relaxed">{li}</li>)}
                            </ul>
                        )}
                    </section>
                ))}
                <FaqBlock faq={a.faq} />
                <CtaBanner />
                <RelatedLinks links={[...related, { name: "Alle Ratgeber-Artikel", path: "/ratgeber" }, { name: "Bewerbung mit KI erstellen", path: "/ki-bewerbung" }]} title="Weiterlesen" />
            </article>
        </PublicLayout>
    );
}
